import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { bindListDelay } from 'reactApp/appHelpers/featuresHelpers/features/bindListDelay';
import { overquotaNewUnsub } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewUnsub';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { updateBillingDataRequest } from 'reactApp/modules/billingData/billingData.module';
import { BuySubscriptionActions } from 'reactApp/modules/buySubscription/buySubscription.module';
import { BillingBuyTemplates } from 'reactApp/modules/buySubscription/buySubscription.types';
import { CardActions } from 'reactApp/modules/creditCard/creditCard.module';
import { CreditCardDmrLinkActions } from 'reactApp/modules/creditCardDmrLink/creditCardDmrLink.module';
import { getHomeFolderItems } from 'reactApp/modules/home/home.selectors';
import { loadHomeFolder } from 'reactApp/modules/home/homeCore.saga';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import {
    getMobileOutflowOffers,
    processMobileOuflowOfferList,
} from 'reactApp/modules/subscriptionRenewal/outflowOffers/subscriptionRenewal.offers.mobile';
import { SubscriptionRenewalActions } from 'reactApp/modules/subscriptionRenewal/subscriptionRenewal.module';
import { SUBSCRIPTION_ANALYTICS } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscription.analytics';
import { type RootState, store } from 'reactApp/store';
import { renderCardModal } from 'reactApp/ui/AddCardModal/AddCardModal.helpers';
import { sendDwh, sendXray } from 'reactApp/utils/ga';
import { channel } from 'redux-saga';
import { call, cancel, put, putResolve, select, takeEvery } from 'redux-saga/effects';

import { logError } from './subscriptionRenewal.helpers';

function* handleCancelAutoRenewMobile({
    payload: { subscription, closeSubscriptionCard, isRebrandingQuotaLanding, isRebranding, dwhData, sendAnalytics },
}: ReturnType<typeof SubscriptionRenewalActions.cancelAutoRenewMobile>) {
    const { isTrial } = subscription;
    emitAnalyticEvent(AnalyticEventNames.MY_TARIFF_CANCEL_AUTOPAY, { isTrial });
    sendDwh({
        ...SUBSCRIPTION_ANALYTICS.MY_TARIFF_CANCEL_AUTOPAYMENT,
        dwhData,
    });

    closeSubscriptionCard();

    const outflowChannel = channel<boolean>();
    const cancelOutflow = () => outflowChannel.close();
    const continueOutflow = () => outflowChannel.put(true);
    const product = yield select((state: RootState) => ProductsSelectors.getProductById(state, subscription.productId));

    const outflowOffers = yield getMobileOutflowOffers(subscription);

    // tempexp_19963-start
    if (overquotaNewUnsub.enable) {
        const homeItems = yield select(getHomeFolderItems);
        if (homeItems.length === 0) {
            yield loadHomeFolder({ id: ROOT_FOLDER_ID, isFolder: true });
        }
    }
    // tempexp_19963-end

    yield processMobileOuflowOfferList(
        outflowOffers,
        {
            product,
            subscription,
            isRebranding,
            isRebrandingQuotaLanding,
            dwhData,
            outflowContext: {},
            cancelOutflow,
            continueOutflow,
            sendAnalytics,
        },
        outflowChannel
    );

    yield cancel();
}

function* handleRenewMobile({
    payload: { dwhData, closeSubscriptionCard, subscription },
}: ReturnType<typeof SubscriptionRenewalActions.renewMobile>) {
    sendDwh({
        ...SUBSCRIPTION_ANALYTICS.MY_TARIFF_TURNON_AUTOPAYMENT,
        dwhData,
    });
    const { id } = subscription;
    try {
        yield putResolve(yield call(BuySubscriptionActions.addAutorenew, id));
        yield put(updateBillingDataRequest({ radar: 'subs' }));
        closeSubscriptionCard();
        yield put(
            showSnackbarAction({
                id: 'renew-subscription',
                closable: true,
                text: 'Подписка восстановлена',
                type: SnackbarTypes.success,
            })
        );
    } catch (error) {
        sendXray(['touch', 'subscription', 'autorenew', 'error']);
        logError(error);
        yield put(
            showSnackbarAction({
                id: 'renew-subscription',
                closable: true,
                text: 'Не удалось восстановить подписку. \nПопробуйте изменить способ оплаты',
                type: SnackbarTypes.failure,
            })
        );
        yield cancel();
    }
}

function* handleAddOrUpdateCardMobile({
    payload: { dwhData, closeSubscriptionCard },
}: ReturnType<typeof SubscriptionRenewalActions.addOrUpdateCardMobile>) {
    sendDwh({
        ...SUBSCRIPTION_ANALYTICS.MY_TARIFF_CHANGE_CARD,
        dwhData,
    });

    closeSubscriptionCard();

    yield renderCardModal({
        onClose() {
            store.dispatch(CreditCardDmrLinkActions.resetLink());
        },
        onSuccess() {
            store.dispatch(CardActions.updateCard({ showNotifications: true, delay: bindListDelay }));
        },
        onUnlinkCard() {
            store.dispatch(CardActions.updateCard({ isSilent: true }));
        },
        size: 'tiny',
        isMobile: true,
        checkoutSkin: BillingBuyTemplates.CLOUD_B2C_MOBILE,
    });
}

export function* watchSubscriptionsRenewalMobile() {
    yield takeEvery(SubscriptionRenewalActions.cancelAutoRenewMobile.toString(), handleCancelAutoRenewMobile);
    yield takeEvery(SubscriptionRenewalActions.renewMobile.toString(), handleRenewMobile);
    yield takeEvery(SubscriptionRenewalActions.addOrUpdateCardMobile.toString(), handleAddOrUpdateCardMobile);
}
